import {
  ChangeAccommodationReservationErrorCase,
  CreateAccommodationReservationErrorCase,
} from "../../../__generated__/gql/graphql";

type CommonAccommodationReservationErrorCase =
  CreateAccommodationReservationErrorCase;
function isCommonAccommodationReservationErrorCase(
  error: unknown,
): error is CommonAccommodationReservationErrorCase {
  return Object.values(CreateAccommodationReservationErrorCase).includes(
    error as CreateAccommodationReservationErrorCase,
  );
}

// 予約作成と予約変更で共通のエラーメッセージ
function reservationErrorMessageOnCreateOrChange(
  error: CommonAccommodationReservationErrorCase,
): [string | null, string | null] {
  switch (error) {
    case CreateAccommodationReservationErrorCase.ResourceUnavailable:
      return [
        "指定された日程に空きがないため、予約を取れません。",
        "別の日程を選択してください",
      ];
    case CreateAccommodationReservationErrorCase.ReservationOverlapError:
      return [
        "すでに持っている予約と重複している期間があるため、予約を取れません。",
        "別の日程を選択してください",
      ];
    case CreateAccommodationReservationErrorCase.MaxReservationsExceededError:
      return [
        "同時に保持できる予約可能数を超えているため、予約を取れません。",
        "新しい予約を取る場合、既に取っている予約を消化するかキャンセルしてください。",
      ];
    case CreateAccommodationReservationErrorCase.MonthlyReservationLimitExceededError:
      return [
        "同月内の予約上限を超えているため、予約を取れません。",
        "新しい予約を取る場合、既に取っている予約を消化するかキャンセルしてください。",
      ];
    case CreateAccommodationReservationErrorCase.InvalidReservationPeriodError:
      // 通常は起き得ないが、例えば12時前に予約カレンダーを開いて、12時以降に予約を取ろうとした場合などに発生する
      return [
        "予約可能な期間で選択されていません。",
        "別の日程を選択してください",
      ];
    // サブスク固有のエラーメッセージ
    case CreateAccommodationReservationErrorCase.CycleDuplicationError:
      return [
        "すでに同一サイクル内に予約があるため、予約を取れません。",
        "別の日程を選択してください",
      ];
    case CreateAccommodationReservationErrorCase.Next7DaysDuplicationError:
      return [
        "すでに直近一週間の予約を1件持っているため、これ以上予約を取れません。",
        "予約を消化（チェックアウト/キャンセル）するか、別の日程を選択してください。",
      ];
    case CreateAccommodationReservationErrorCase.MaxNightsPerMonthExceededError:
      return [
        "1ヶ月あたりの利用泊数を超えているため、予約を取れません。",
        "組織管理者にお問い合わせください。",
      ];
    case CreateAccommodationReservationErrorCase.MaxNightsPerYearExceededError:
      return [
        "1年間あたりの利用泊数を超えているため、予約を取れません。",
        "組織管理者にお問い合わせください。",
      ];
    // オーナー固有のエラーメッセージ
    case CreateAccommodationReservationErrorCase.OwnerStayCreditInsufficientError:
      return [
        "ご利用可能な泊数を消化したため、予約を取れません。",
        "すでにある予約をキャンセルするか、次年度までお待ちください。",
      ];
    case CreateAccommodationReservationErrorCase.TrialStayCreditInsufficientError:
      return [
        "Trialプランの利用可能な泊数を消化したため、予約を取れません。",
        "すでにある予約をキャンセルしてください。",
      ];
    case CreateAccommodationReservationErrorCase.PeakDaysLimitExceededError:
      return [
        "ご利用可能な繁忙日を超えているため、予約を取れません。",
        "別の日程を選択してください。",
      ];
    case CreateAccommodationReservationErrorCase.PeakDayOwnerOnlyError:
      return [
        "オーナー拠点の繁忙日はオーナー会員のみが利用できます",
        "別の日程を選択してください。",
      ];
    case CreateAccommodationReservationErrorCase.SiteHighSeasonLimitExceededError:
      return [
        "所有する部屋のハイシーズンにご利用可能な泊数を超えているため、予約を取れません。",
        "別の日程を選択してください。",
      ];
    case CreateAccommodationReservationErrorCase.TrialContractSuspended:
      return [
        "お支払いの確認が取れていないため、予約を取れません。",
        "お支払い方法をご確認ください。",
      ];
    case CreateAccommodationReservationErrorCase.SubscriptionSuspended:
      return [
        "お支払いの確認が取れていないため、予約を取れません。",
        "お支払い方法をご確認ください。",
      ];
    case CreateAccommodationReservationErrorCase.UnpaidAccommodationFeeError:
      return [
        "未払いの宿泊料金があるため、予約を取れません。",
        "お支払いを完了してから予約をお取りください。",
      ];
    // チケット固有のエラーメッセージ
    case CreateAccommodationReservationErrorCase.StayTicketError:
      return [
        "現在のチケットでご希望の予約を取れません",
        "別のものを選択してください",
      ];
    // Stay Gift from Owners のオーナーが自身の予約を取ろうとした
    case CreateAccommodationReservationErrorCase.OwnerOfStayGiftFromOwnersError:
      return [
        "SANU 2nd Home に招待したい方へ、URL を共有してください。",
        "このチケットはご本人ではご利用いただけません。URL を受け取った方が、ご自身で予約を行えます。",
      ];
    // SanuIncプロバイダーでstaffGroupIdが指定されていない（管理者向け）
    case CreateAccommodationReservationErrorCase.SanuIncStaffGroupIdRequiredError:
      return [
        "グループの指定が必要です",
        "SanuIncプロバイダーでの予約には、グループを選択してください。",
      ];
    // Weekdayプランの新規約に同意していない
    case CreateAccommodationReservationErrorCase.WeekdayTermsNotAgreedError:
      return [
        "新しい利用規約への同意が必要です",
        "予約を取るには、利用規約の改定内容をご確認のうえ、同意をお願いいたします。",
      ];
    // 未同意の規約の改定がある
    case CreateAccommodationReservationErrorCase.TermsNotAgreedError:
      return [
        "新しい利用規約への同意が必要です",
        "予約の操作には、利用規約の改定内容をご確認のうえ、同意をお願いいたします。",
      ];
    // Weekdayプランの月7泊制限を超過している
    case CreateAccommodationReservationErrorCase.MonthlyNightLimitExceededError:
      return [
        "ひと月の予約上限に達しているため、予約を取れません。",
        "別の月を選択してください。",
      ];
    // 例外
    case CreateAccommodationReservationErrorCase.UnexpectedError:
      return [
        "予期しないエラーが発生しました",
        "再度試すか、窓口よりお問い合わせください",
      ];
    default: {
      const _exhaustiveCheck: never = error;
      return [
        "予期しないエラーが発生しました",
        "再度試すか、窓口よりお問い合わせください",
      ];
    }
  }
}

export function reservationErrorMessageOnCreate(
  error: CreateAccommodationReservationErrorCase,
): [string | null, string | null] {
  return reservationErrorMessageOnCreateOrChange(error);
}

export function reservationErrorMessageOnChange(
  error: ChangeAccommodationReservationErrorCase,
): [string | null, string | null] {
  if (isCommonAccommodationReservationErrorCase(error)) {
    return reservationErrorMessageOnCreateOrChange(error);
  }
  // 予約変更特有のエラーメッセージ
  switch (error) {
    case ChangeAccommodationReservationErrorCase.ChangePaymentFailed:
      return [
        "予約変更の支払いに失敗しました",
        "ご登録いただいているクレジットカードが利用できない可能性があります。",
      ];
    case ChangeAccommodationReservationErrorCase.MutationScopeError:
      return [
        "ご希望の予約変更は現在できません",
        "別の部屋・日程をお試しください。",
      ];
    case ChangeAccommodationReservationErrorCase.HasConflictingFacilityReservation:
      return [
        "変更先に対応していない設備予約があります。",
        "先に設備予約をキャンセルしてから予約を変更してください。",
      ];
    default: {
      const _exhaustiveCheck: never = error;
      return [
        "予期しないエラーが発生しました",
        "再度試すか、窓口よりお問い合わせください",
      ];
    }
  }
}
